import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'
import {
  IMMIGRATION_STATUS_CHOICES,
  DOCUMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  METAL_LEVELS,
} from '../../helpers/catalogs'

/**
 * Catálogo de Destinatarios (`/recipients`). Namespace raíz de la Fase 1.9:
 * `comms.*` — mensajería (chat, campañas, plantillas, destinatarios,
 * automatizaciones).
 *
 * No se traducen: nombres y descripciones de etiquetas y segmentos (datos de
 * BD), `subscriptionStatus` crudo, ni los mensajes de error del backend.
 */
export const RECIPIENT_MESSAGES = defineMessages({
  // ── Página ──
  recipients: {id: 'MENU.item.recipients', defaultMessage: 'Recipients'},
  effectiveYear: {id: 'comms.recipients.page.effectiveYear', defaultMessage: 'Effective year:'},
  allSegments: {id: 'comms.recipients.page.allSegments', defaultMessage: 'All Segments'},
  manageSegments: {id: 'comms.recipients.page.manageSegments', defaultMessage: 'Manage Segments'},
  exportCsv: {id: 'comms.recipients.page.exportCsv', defaultMessage: 'Export CSV'},
  addRecipients: {id: 'comms.recipients.page.addRecipients', defaultMessage: 'Add Recipients'},
  csvSubscriptionStatus: {
    id: 'comms.recipients.csv.subscriptionStatus',
    defaultMessage: 'Subscription Status',
  },

  // ── Barra lateral de filtros ──
  filtersTitle: {id: 'comms.recipients.filters.title', defaultMessage: 'Filters'},
  allRecipients: {id: 'comms.recipients.filters.all', defaultMessage: 'All Recipients'},
  subscribed: {id: 'comms.recipients.status.subscribed', defaultMessage: 'Subscribed'},
  unsubscribed: {id: 'comms.recipients.status.unsubscribed', defaultMessage: 'Unsubscribed'},
  noTagsYet: {id: 'comms.recipients.filters.noTags', defaultMessage: 'No tags yet'},
  segments: {id: 'comms.recipients.filters.segments', defaultMessage: 'Segments'},
  noSegmentsYet: {id: 'comms.recipients.filters.noSegments', defaultMessage: 'No segments yet'},

  // ── Listado ──
  searchPlaceholder: {
    id: 'comms.recipients.list.searchPlaceholder',
    defaultMessage: 'Search by name, email, or phone...',
  },
  clearAll: {id: 'comms.recipients.list.clearAll', defaultMessage: 'Clear all'},
  contactsSelected: {
    id: 'comms.recipients.list.selected',
    defaultMessage:
      '<b>{count}</b> {count, plural, one {contact selected} other {contacts selected}}',
  },
  removing: {id: 'comms.recipients.list.removing', defaultMessage: 'Removing...'},
  removeFromRecipients: {
    id: 'comms.recipients.list.removeFromRecipients',
    defaultMessage: 'Remove from Recipients',
  },
  unsubscribing: {id: 'comms.recipients.list.unsubscribing', defaultMessage: 'Unsubscribing...'},
  bulkUnsubscribe: {
    id: 'comms.recipients.list.bulkUnsubscribe',
    defaultMessage: 'Bulk Unsubscribe',
  },
  clearSelection: {id: 'comms.recipients.list.clearSelection', defaultMessage: 'Clear Selection'},
  listEmpty: {id: 'comms.recipients.list.empty', defaultMessage: 'No recipients found'},
  noTags: {id: 'comms.recipients.list.noTags', defaultMessage: 'No tags'},
  unsubscribe: {id: 'comms.recipients.list.unsubscribe', defaultMessage: 'Unsubscribe'},
  confirmUnsubscribeTitle: {
    id: 'comms.recipients.list.confirmUnsubscribeTitle',
    defaultMessage: 'Confirm unsubscribe',
  },
  confirmUnsubscribeBody: {
    id: 'comms.recipients.list.confirmUnsubscribeBody',
    defaultMessage:
      'This contact will be unsubscribed from all campaigns. Do you want to continue?',
  },
  confirmBulkDelete: {
    id: 'comms.recipients.list.confirmBulkDelete',
    defaultMessage:
      'Are you sure you want to delete {count, plural, one {# contact} other {# contacts}}?',
  },
  bulkDeleteNotImplemented: {
    id: 'comms.recipients.list.bulkDeleteNotImplemented',
    defaultMessage: 'Bulk delete not implemented yet',
  },
  confirmRemove: {
    id: 'comms.recipients.list.confirmRemove',
    defaultMessage:
      'Remove {count, plural, one {# contact} other {# contacts}} from recipients list?',
  },
  confirmBulkUnsubscribe: {
    id: 'comms.recipients.list.confirmBulkUnsubscribe',
    defaultMessage:
      'Unsubscribe {count, plural, one {# contact} other {# contacts}} from all campaigns?',
  },

  // ── Chips de filtros aplicados ──
  chipValue: {id: 'comms.recipients.chip.value', defaultMessage: '{label}: {value}'},
  chipCount: {id: 'comms.recipients.chip.count', defaultMessage: '{label} ({count})'},
  chipPolicyHolder: {id: 'comms.recipients.chip.policyHolder', defaultMessage: 'Policy Holder'},
  chipWithoutPolicy: {
    id: 'comms.recipients.chip.withoutPolicy',
    defaultMessage: 'Without Policy',
  },

  // ── Filtros avanzados (lo que no existe ya en policies.list.filters.*) ──
  policyRelationship: {
    id: 'comms.recipients.filters.policyRelationship',
    defaultMessage: 'Policy Relationship',
  },
  optAllContacts: {id: 'comms.recipients.filters.allContacts', defaultMessage: 'All contacts'},
  optPolicyHolders: {
    id: 'comms.recipients.filters.policyHolders',
    defaultMessage: 'Policy holders',
  },
  optFamilyMembers: {
    id: 'comms.recipients.filters.familyMembers',
    defaultMessage: 'Family members',
  },
  optNoPolicy: {id: 'comms.recipients.filters.noPolicy', defaultMessage: 'No policy'},
  sectionDateRanges: {
    id: 'comms.recipients.filters.dateRanges',
    defaultMessage: 'Date Ranges',
  },
  policyCreatedFrom: {
    id: 'comms.recipients.filters.policyCreatedFrom',
    defaultMessage: 'Policy Created From',
  },
  policyCreatedTo: {
    id: 'comms.recipients.filters.policyCreatedTo',
    defaultMessage: 'Policy Created To',
  },
  submissionDate: {
    id: 'comms.recipients.filters.submissionDate',
    defaultMessage: 'Submission Date',
  },
  submissionDateFrom: {
    id: 'comms.recipients.filters.submissionDateFrom',
    defaultMessage: 'Submission Date From',
  },
  submissionDateTo: {
    id: 'comms.recipients.filters.submissionDateTo',
    defaultMessage: 'Submission Date To',
  },

  // ── Modal de agregar destinatarios ──
  addHint: {
    id: 'comms.recipients.add.hint',
    defaultMessage:
      'Select contacts to add to your recipients list. Only contacts not already in the list are shown.',
  },
  addEmpty: {
    id: 'comms.recipients.add.empty',
    defaultMessage:
      'No contacts available to add. All contacts are already in your recipients list.',
  },
  addShowing: {
    id: 'comms.recipients.add.showing',
    defaultMessage: 'Showing {shown} of {total} contacts',
  },
  adding: {id: 'comms.recipients.add.adding', defaultMessage: 'Adding...'},
  addAction: {
    id: 'comms.recipients.add.action',
    defaultMessage: '{count, plural, =0 {Add} other {Add (#)}}',
  },

  // ── Modal de etiquetas masivas ──
  bulkTagTitle: {
    id: 'comms.recipients.bulkTag.title',
    defaultMessage: 'Bulk Tag Management',
  },
  bulkTagAssign: {
    id: 'comms.recipients.bulkTag.assign',
    defaultMessage: 'Add tags to selected contacts',
  },
  bulkTagUnassign: {
    id: 'comms.recipients.bulkTag.unassign',
    defaultMessage: 'Remove tags from selected contacts',
  },
  bulkTagSelect: {id: 'comms.recipients.bulkTag.select', defaultMessage: 'Select Tags'},
  bulkTagNoTags: {
    id: 'comms.recipients.bulkTag.noTags',
    defaultMessage: 'No tags available. Create tags first in Tag Manager.',
  },
  processing: {id: 'comms.recipients.bulkTag.processing', defaultMessage: 'Processing...'},
  bulkTagAdd: {id: 'comms.recipients.bulkTag.add', defaultMessage: 'Add Tags'},
  bulkTagRemove: {id: 'comms.recipients.bulkTag.remove', defaultMessage: 'Remove Tags'},

  // ── Gestor de etiquetas ──
  tagName: {id: 'comms.recipients.tag.name', defaultMessage: 'Tag Name'},
  tagNamePlaceholder: {
    id: 'comms.recipients.tag.namePlaceholder',
    defaultMessage: 'e.g., VIP, Lead, Hot',
  },
  optionalPlaceholder: {id: 'comms.recipients.tag.optional', defaultMessage: 'Optional'},
  updateTag: {id: 'comms.recipients.tag.update', defaultMessage: 'Update Tag'},
  createTag: {id: 'comms.recipients.tag.create', defaultMessage: 'Create Tag'},
  existingTags: {
    id: 'comms.recipients.tag.existing',
    defaultMessage: 'Existing Tags ({count})',
  },
  tagsEmpty: {
    id: 'comms.recipients.tag.empty',
    defaultMessage: 'No tags created yet. Create your first tag above.',
  },
  contactsColumn: {id: 'comms.recipients.field.contacts', defaultMessage: 'Contacts'},
  confirmDeleteTag: {
    id: 'comms.recipients.tag.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this tag?',
  },

  // ── Gestor de segmentos ──
  segmentsHint: {
    id: 'comms.recipients.segment.hint',
    defaultMessage: 'Create dynamic segments to organize contacts based on conditions',
  },
  createSegment: {id: 'comms.recipients.segment.create', defaultMessage: 'Create Segment'},
  segmentsEmpty: {
    id: 'comms.recipients.segment.empty',
    defaultMessage: 'No segments created yet. Click "Create Segment" to get started.',
  },
  segmentConditions: {
    id: 'comms.recipients.segment.conditionCount',
    defaultMessage: '{count, plural, one {# condition} other {# conditions}}',
  },
  matchAll: {id: 'comms.recipients.segment.matchAll', defaultMessage: 'Match ALL'},
  matchAny: {id: 'comms.recipients.segment.matchAny', defaultMessage: 'Match ANY'},
  editSegment: {id: 'comms.recipients.segment.editTitle', defaultMessage: 'Edit Segment'},
  newSegment: {id: 'comms.recipients.segment.newTitle', defaultMessage: 'Create New Segment'},
  segmentName: {id: 'comms.recipients.segment.name', defaultMessage: 'Segment Name'},
  segmentNamePlaceholder: {
    id: 'comms.recipients.segment.namePlaceholder',
    defaultMessage: 'e.g., VIP Customers',
  },
  segmentDescPlaceholder: {
    id: 'comms.recipients.segment.descPlaceholder',
    defaultMessage: 'Optional description',
  },
  matchType: {id: 'comms.recipients.segment.matchType', defaultMessage: 'Match Type'},
  matchAllOption: {
    id: 'comms.recipients.segment.matchAllOption',
    defaultMessage: 'Match ALL conditions (AND)',
  },
  matchAnyOption: {
    id: 'comms.recipients.segment.matchAnyOption',
    defaultMessage: 'Match ANY condition (OR)',
  },
  conditions: {id: 'comms.recipients.segment.conditions', defaultMessage: 'Conditions'},
  addCondition: {id: 'comms.recipients.segment.addCondition', defaultMessage: 'Add Condition'},
  noConditions: {
    id: 'comms.recipients.segment.noConditions',
    defaultMessage:
      'No conditions added yet. Click "Add Condition" to start building your segment.',
  },
  conditionTag: {id: 'comms.recipients.segment.fieldTag', defaultMessage: 'Tag'},
  conditionContains: {
    id: 'comms.recipients.segment.opContains',
    defaultMessage: 'Contains',
  },
  conditionNotContains: {
    id: 'comms.recipients.segment.opNotContains',
    defaultMessage: 'Not Contains',
  },
  conditionEquals: {id: 'comms.recipients.segment.opEquals', defaultMessage: 'Equals'},
  selectTag: {id: 'comms.recipients.segment.selectTag', defaultMessage: 'Select a tag...'},
  valuePlaceholder: {id: 'comms.recipients.segment.value', defaultMessage: 'Value'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  updateSegment: {id: 'comms.recipients.segment.update', defaultMessage: 'Update Segment'},
  confirmDeleteSegment: {
    id: 'comms.recipients.segment.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this segment?',
  },

  // ── Toasts ──
  toastSelectContact: {
    id: 'comms.recipients.toast.selectContact',
    defaultMessage: 'Please select at least one contact',
  },
  toastAdded: {
    id: 'comms.recipients.toast.added',
    defaultMessage: '{count, plural, one {# contact} other {# contacts}} added to recipients',
  },
  toastErrorAdd: {
    id: 'comms.recipients.toast.errorAdd',
    defaultMessage: 'Failed to add contacts to recipients',
  },
  toastSelectTag: {
    id: 'comms.recipients.toast.selectTag',
    defaultMessage: 'Please select at least one tag',
  },
  toastTagsAssigned: {
    id: 'comms.recipients.toast.tagsAssigned',
    defaultMessage: '{count, plural, one {# tag} other {# tags}} assigned successfully',
  },
  toastTagsRemoved: {
    id: 'comms.recipients.toast.tagsRemoved',
    defaultMessage: '{count, plural, one {# tag} other {# tags}} removed successfully',
  },
  toastErrorTags: {
    id: 'comms.recipients.toast.errorTags',
    defaultMessage: 'Failed to update tags',
  },
  toastRemoved: {
    id: 'comms.recipients.toast.removed',
    defaultMessage: '{count, plural, one {# contact} other {# contacts}} removed',
  },
  toastErrorRemove: {
    id: 'comms.recipients.toast.errorRemove',
    defaultMessage: 'Failed to remove from recipients',
  },
  toastUnsubscribedCount: {
    id: 'comms.recipients.toast.unsubscribedCount',
    defaultMessage: '{count, plural, one {# contact} other {# contacts}} unsubscribed',
  },
  toastUnsubscribedOne: {
    id: 'comms.recipients.toast.unsubscribedOne',
    defaultMessage: 'Contact unsubscribed',
  },
  toastErrorUnsubscribeBulk: {
    id: 'comms.recipients.toast.errorUnsubscribeBulk',
    defaultMessage: 'Failed to unsubscribe contacts',
  },
  toastErrorUnsubscribeOne: {
    id: 'comms.recipients.toast.errorUnsubscribeOne',
    defaultMessage: 'Failed to unsubscribe contact',
  },
  toastTagUpdated: {
    id: 'comms.recipients.toast.tagUpdated',
    defaultMessage: 'Tag updated successfully',
  },
  toastTagCreated: {
    id: 'comms.recipients.toast.tagCreated',
    defaultMessage: 'Tag created successfully',
  },
  toastTagDeleted: {
    id: 'comms.recipients.toast.tagDeleted',
    defaultMessage: 'Tag deleted successfully',
  },
  toastErrorSaveTag: {
    id: 'comms.recipients.toast.errorSaveTag',
    defaultMessage: 'Failed to save tag',
  },
  toastErrorDeleteTag: {
    id: 'comms.recipients.toast.errorDeleteTag',
    defaultMessage: 'Failed to delete tag',
  },
  toastSegmentNameRequired: {
    id: 'comms.recipients.toast.segmentNameRequired',
    defaultMessage: 'Segment name is required',
  },
  toastSegmentConditionRequired: {
    id: 'comms.recipients.toast.segmentConditionRequired',
    defaultMessage: 'At least one condition is required',
  },
  toastSegmentUpdated: {
    id: 'comms.recipients.toast.segmentUpdated',
    defaultMessage: 'Segment updated successfully',
  },
  toastSegmentCreated: {
    id: 'comms.recipients.toast.segmentCreated',
    defaultMessage: 'Segment created successfully',
  },
  toastSegmentDeleted: {
    id: 'comms.recipients.toast.segmentDeleted',
    defaultMessage: 'Segment deleted successfully',
  },
  toastErrorSaveSegment: {
    id: 'comms.recipients.toast.errorSaveSegment',
    defaultMessage: 'Failed to save segment',
  },
  toastErrorDeleteSegment: {
    id: 'comms.recipients.toast.errorDeleteSegment',
    defaultMessage: 'Failed to delete segment',
  },

  // ── Reutilizadas de otros namespaces (ya extraídas allí) ──
  tags: {id: 'comms.tags.label', defaultMessage: 'Tags'},
  manageTags: {id: 'comms.tags.manage', defaultMessage: 'Manage Tags'},
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  action: {id: 'common.field.action', defaultMessage: 'Action'},
  description: {id: 'common.field.description', defaultMessage: 'Description'},
  color: {id: 'common.field.color', defaultMessage: 'Color'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  delete: {id: 'common.action.delete', defaultMessage: 'Delete'},
  view: {id: 'common.action.view', defaultMessage: 'View'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},
  filter: {id: 'common.action.filter', defaultMessage: 'Filter'},
  clearFilters: {id: 'common.action.clearFilters', defaultMessage: 'Clear filters'},
  previous: {id: 'common.action.previous', defaultMessage: 'Previous'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  pageOf: {id: 'common.pagination.pageOf', defaultMessage: 'Page {page} of {total}'},
})

/**
 * Llaves que **ya declara** `policies/components/ModalFilters.tsx` (Fase 1.1) y que
 * este módulo reutiliza tal cual: los filtros avanzados de destinatarios son el
 * mismo conjunto de campos que el de pólizas (guía §17.12 y §17.13 — el chip del
 * filtro aplicado debe decir exactamente lo mismo que el modal).
 *
 * OJO: **a propósito no van dentro de un `defineMessages`**. Si se declararan aquí
 * el extractor las vería dos veces; como ya se extraen desde `ModalFilters`, aquí
 * solo se referencian.
 */
export const POLICY_FILTER_MESSAGES = {
  title: {id: 'policies.list.filters.title', defaultMessage: 'Advanced Filters'},
  clear: {id: 'policies.list.filters.action.clear', defaultMessage: 'Clear'},
  applyFilters: {
    id: 'policies.list.filters.action.applyFilters',
    defaultMessage: 'Apply Filters',
  },
  removeFilter: {
    id: 'policies.list.filters.removeFilter',
    defaultMessage: 'Remove {label} filter',
  },
  sectionCarrierImport: {
    id: 'policies.list.filters.section.carrierImport',
    defaultMessage: 'Carrier Import Filters',
  },

  // Campos
  agent: {id: 'common.field.agent', defaultMessage: 'Agent'},
  assignedTo: {id: 'policies.list.filters.field.assignedTo', defaultMessage: 'Assigned To'},
  carrier: {id: 'policies.list.filters.field.carrier', defaultMessage: 'Carrier'},
  state: {id: 'common.field.state', defaultMessage: 'State'},
  stateJurisdictionType: {
    id: 'policies.list.filters.field.stateJurisdictionType',
    defaultMessage: 'State Jurisdiction Type',
  },
  zipCode: {id: 'common.field.zipCode', defaultMessage: 'Zip Code'},
  policyStatus: {
    id: 'policies.list.filters.field.policyStatus',
    defaultMessage: 'Policy Status',
  },
  policyTypes: {id: 'policies.list.filters.field.policyTypes', defaultMessage: 'Policy Types'},
  unassignedPolicies: {
    id: 'policies.list.filters.field.unassignedPolicies',
    defaultMessage: 'Unassigned Policies',
  },
  source: {id: 'policies.list.filters.field.source', defaultMessage: 'Source'},
  metalLevel: {id: 'policies.list.filters.field.metalLevel', defaultMessage: 'Metal Level'},
  immigrationStatus: {
    id: 'policies.list.filters.field.immigrationStatus',
    defaultMessage: 'Immigration Status',
  },
  documentStatus: {
    id: 'policies.list.filters.field.documentStatus',
    defaultMessage: 'Document Status',
  },
  paymentStatus: {
    id: 'policies.list.filters.field.paymentStatus',
    defaultMessage: 'Payment status',
  },
  genderHolder: {
    id: 'policies.list.filters.field.genderHolder',
    defaultMessage: 'Gender (Holder)',
  },
  memberIdStatus: {
    id: 'policies.list.filters.field.memberIdStatus',
    defaultMessage: 'Member ID Status',
  },
  commissionReportStatus: {
    id: 'policies.list.filters.field.commissionReportStatus',
    defaultMessage: 'Commission Report Status',
  },
  commissionPaymentStatus: {
    id: 'policies.list.filters.field.commissionPaymentStatus',
    defaultMessage: 'Commission Payment Status',
  },
  ownsCar: {id: 'policies.list.filters.field.ownsCar', defaultMessage: 'Owns car'},
  ownsHome: {id: 'policies.list.filters.field.ownsHome', defaultMessage: 'Owns home'},
  hasAutoPolicy: {
    id: 'policies.list.filters.field.hasAutoPolicy',
    defaultMessage: 'Has auto policy',
  },
  hasHomePolicy: {
    id: 'policies.list.filters.field.hasHomePolicy',
    defaultMessage: 'Has home policy',
  },
  carrierStatus: {
    id: 'policies.list.filters.field.carrierStatus',
    defaultMessage: 'Carrier Status',
  },
  ageIn: {id: 'policies.list.filters.field.ageIn', defaultMessage: 'Age-In'},
  delinquent: {id: 'policies.list.filters.field.delinquent', defaultMessage: 'Delinquent'},
  unpaidPending: {
    id: 'policies.list.filters.field.unpaidPending',
    defaultMessage: 'Unpaid Pending',
  },
  unpaidPastDue: {
    id: 'policies.list.filters.field.unpaidPastDue',
    defaultMessage: 'Unpaid Past Due',
  },
  createdDate: {
    id: 'policies.list.filters.dateRange.created',
    defaultMessage: 'Created Date',
  },
  carrierAsOfDate: {
    id: 'policies.list.filters.dateRange.carrierAsOf',
    defaultMessage: 'Carrier As-of Date',
  },
  carrierAsOfFrom: {
    id: 'policies.list.filters.date.carrierAsOfFrom',
    defaultMessage: 'Carrier As-of Date From',
  },
  carrierAsOfTo: {
    id: 'policies.list.filters.date.carrierAsOfTo',
    defaultMessage: 'Carrier As-of Date To',
  },

  // Opciones y placeholders
  allPolicies: {id: 'policies.list.filters.opt.allPolicies', defaultMessage: 'All policies'},
  onlyUnassigned: {
    id: 'policies.list.filters.opt.onlyUnassigned',
    defaultMessage: 'Only unassigned',
  },
  onlyAssigned: {id: 'policies.list.filters.opt.onlyAssigned', defaultMessage: 'Only assigned'},
  federal: {id: 'policies.list.filters.opt.federal', defaultMessage: 'Federal'},
  stateJurisdiction: {
    id: 'policies.list.filters.opt.stateJurisdiction',
    defaultMessage: 'State',
  },
  withMemberId: {
    id: 'policies.list.filters.opt.withMemberId',
    defaultMessage: 'With Member ID',
  },
  withoutMemberId: {
    id: 'policies.list.filters.opt.withoutMemberId',
    defaultMessage: 'Without Member ID',
  },
  consideredCommission: {
    id: 'policies.list.filters.opt.consideredCommission',
    defaultMessage: 'Considered for commission',
  },
  notConsideredCommission: {
    id: 'policies.list.filters.opt.notConsideredCommission',
    defaultMessage: 'Not considered for commission',
  },
  placeholderAny: {id: 'policies.list.filters.placeholder.any', defaultMessage: 'Any'},
  placeholderSelectJurisdiction: {
    id: 'policies.list.filters.placeholder.selectJurisdiction',
    defaultMessage: 'Select jurisdiction type',
  },
  placeholderSelectMetalLevel: {
    id: 'policies.list.filters.placeholder.selectMetalLevel',
    defaultMessage: 'Select Metal Level',
  },
  placeholderSelectCarrierStatus: {
    id: 'policies.list.filters.placeholder.selectCarrierStatus',
    defaultMessage: 'Select carrier status...',
  },
  selectOption: {
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  },
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},

  // Estados en aseguradora (CARRIER_STATUS_OPTIONS)
  csInForce: {id: 'policies.list.filters.cs.inForce', defaultMessage: 'In Force'},
  csTerminated: {id: 'policies.list.filters.cs.terminated', defaultMessage: 'Terminated'},
  csGracePeriod: {id: 'policies.list.filters.cs.gracePeriod', defaultMessage: 'Grace Period'},
  csPendingEffectuation: {
    id: 'policies.list.filters.cs.pendingEffectuation',
    defaultMessage: 'Pending Effectuation',
  },
  csPastDue: {id: 'policies.list.filters.cs.pastDue', defaultMessage: 'Past Due'},
  csDelinquent: {id: 'policies.list.filters.cs.delinquent', defaultMessage: 'Delinquent'},
  csFutureEffective: {
    id: 'policies.list.filters.cs.futureEffective',
    defaultMessage: 'Future Effective',
  },
  csUnknown: {id: 'policies.list.filters.cs.unknown', defaultMessage: 'Unknown'},

  // Estado de pago de comisión (COMMISSION_PAYMENT_STATUS_OPTIONS)
  segNeverStarted: {
    id: 'policies.list.filters.seg.neverStarted',
    defaultMessage: 'Never Started',
  },
  segCanceled: {id: 'policies.list.filters.seg.canceled', defaultMessage: 'Canceled'},
  segSinComision: {id: 'policies.list.filters.seg.sinComision', defaultMessage: 'Sin Comisión'},
  segStandBy: {id: 'policies.list.filters.seg.standBy', defaultMessage: 'Stand By'},

  // Estados de póliza que usan los chips (POLICY_STATUS_LABELS)
  psActive: {id: 'policies.list.filters.ds.active', defaultMessage: 'Active'},
  psPending: {id: 'policies.list.filters.ds.pending', defaultMessage: 'Pending'},
  psCompleted: {id: 'policies.list.filters.ds.completed', defaultMessage: 'Completed'},

  // Catálogos compartidos (helpers/catalogs.ts) — no se muta el const (§17.10)
  metalPlatinum: {id: 'policies.list.filters.metal.platinum', defaultMessage: 'Platinum'},
  metalGold: {id: 'policies.list.filters.metal.gold', defaultMessage: 'Gold'},
  metalSilver: {id: 'policies.list.filters.metal.silver', defaultMessage: 'Silver'},
  metalBronze: {id: 'policies.list.filters.metal.bronze', defaultMessage: 'Bronze'},
  metalCatastrophic: {
    id: 'policies.list.filters.metal.catastrophic',
    defaultMessage: 'Catastrophic',
  },
  genderMale: {id: 'common.gender.male', defaultMessage: 'Male'},
  genderFemale: {id: 'common.gender.female', defaultMessage: 'Female'},
  genderOther: {id: 'common.option.other', defaultMessage: 'Other'},
  dsActive: {id: 'policies.list.filters.ds.active', defaultMessage: 'Active'},
  dsPending: {id: 'policies.list.filters.ds.pending', defaultMessage: 'Pending'},
  dsProcessing: {id: 'policies.list.filters.ds.processing', defaultMessage: 'Processing'},
  dsDeclined: {id: 'policies.list.filters.ds.declined', defaultMessage: 'Declined'},
  dsCompleted: {id: 'policies.list.filters.ds.completed', defaultMessage: 'Completed'},
  psPaid: {id: 'policies.list.filters.ps.paid', defaultMessage: 'Paid'},
  psFailed: {id: 'policies.list.filters.ps.failed', defaultMessage: 'Failed'},
  psPaymentPending: {id: 'policies.list.filters.ps.pending', defaultMessage: 'Pending'},
  psAutoPay: {id: 'policies.list.filters.ps.autoPay', defaultMessage: 'Auto Pay'},
  psNotApplicable: {
    id: 'policies.list.filters.ps.notApplicable',
    defaultMessage: 'Not Applicable ($0)',
  },
  psPagaPorSuCuenta: {
    id: 'policies.list.filters.ps.pagaPorSuCuenta',
    defaultMessage: 'Paga por su cuenta',
  },
  imCitizen: {id: 'policies.list.filters.im.citizen', defaultMessage: 'Citizen'},
  imResident: {id: 'policies.list.filters.im.resident', defaultMessage: 'Resident'},
  imAsylum: {id: 'policies.list.filters.im.asylum', defaultMessage: 'Asylum Seeker'},
  imEad: {
    id: 'policies.list.filters.im.ead',
    defaultMessage: 'Employment Authorization Document (EAD)',
  },
  imTps: {
    id: 'policies.list.filters.im.tps',
    defaultMessage: 'Temporary Protected Status (TPS)',
  },
  imHumanitarianParole: {
    id: 'policies.list.filters.im.humanitarianParole',
    defaultMessage: 'Humanitarian Parole',
  },
  imUndocumented: {
    id: 'policies.list.filters.im.undocumented',
    defaultMessage: 'Undocumented',
  },
}

/** Símbolos sueltos: diseño, no texto (guía §17.3). */
export const EM_DASH = '—'
export const CROSS_MARK = '×'
export const REQUIRED_MARK = '*'
export const SPACE = ' '
/** Separador de los metadatos de un segmento (`3 condiciones • Match ALL`). */
export const BULLET_SEPARATOR = ' • '

/** Opciones de estado en aseguradora (antes `CARRIER_STATUS_OPTIONS` literal). */
const CARRIER_STATUS_MESSAGES: {value: string; message: MessageDescriptor}[] = [
  {value: 'IN_FORCE', message: POLICY_FILTER_MESSAGES.csInForce},
  {value: 'TERMINATED', message: POLICY_FILTER_MESSAGES.csTerminated},
  {value: 'GRACE_PERIOD', message: POLICY_FILTER_MESSAGES.csGracePeriod},
  {value: 'PENDING_EFFECTUATION', message: POLICY_FILTER_MESSAGES.csPendingEffectuation},
  {value: 'PAST_DUE', message: POLICY_FILTER_MESSAGES.csPastDue},
  {value: 'DELINQUENT', message: POLICY_FILTER_MESSAGES.csDelinquent},
  {value: 'FUTURE_EFFECTIVE', message: POLICY_FILTER_MESSAGES.csFutureEffective},
  {value: 'UNKNOWN', message: POLICY_FILTER_MESSAGES.csUnknown},
]

/** Opciones de estado de pago de comisión (antes `COMMISSION_PAYMENT_STATUS_OPTIONS`). */
const COMMISSION_STATUS_MESSAGES: {value: string; message: MessageDescriptor}[] = [
  {value: 'NEVER_STARTED', message: POLICY_FILTER_MESSAGES.segNeverStarted},
  {value: 'CANCELED', message: POLICY_FILTER_MESSAGES.segCanceled},
  {value: 'SIN_COMISION', message: POLICY_FILTER_MESSAGES.segSinComision},
  {value: 'STAND_BY', message: POLICY_FILTER_MESSAGES.segStandBy},
]

/** `value` de los catálogos compartidos → descriptor traducido (guía §17.10: no se muta el const). */
const CATALOG_MESSAGES: Record<string, MessageDescriptor> = {
  // GENDER_OPTIONS
  Male: POLICY_FILTER_MESSAGES.genderMale,
  Female: POLICY_FILTER_MESSAGES.genderFemale,
  Other: POLICY_FILTER_MESSAGES.genderOther,
  // METAL_LEVELS
  '5': POLICY_FILTER_MESSAGES.metalPlatinum,
  '4': POLICY_FILTER_MESSAGES.metalGold,
  '3': POLICY_FILTER_MESSAGES.metalSilver,
  '2': POLICY_FILTER_MESSAGES.metalBronze,
  '1': POLICY_FILTER_MESSAGES.metalCatastrophic,
  // IMMIGRATION_STATUS_CHOICES
  CITIZEN: POLICY_FILTER_MESSAGES.imCitizen,
  RESIDENT: POLICY_FILTER_MESSAGES.imResident,
  ASYLUM: POLICY_FILTER_MESSAGES.imAsylum,
  EAD: POLICY_FILTER_MESSAGES.imEad,
  TPS: POLICY_FILTER_MESSAGES.imTps,
  HUMANITARIAN_PAROLE: POLICY_FILTER_MESSAGES.imHumanitarianParole,
  OTHER: POLICY_FILTER_MESSAGES.genderOther,
  UNDOCUMENTED: POLICY_FILTER_MESSAGES.imUndocumented,
  // DOCUMENT_STATUS_OPTIONS / PAYMENT_STATUS_OPTIONS comparten valores con los de arriba,
  // así que se resuelven por prefijo en `translateOptions`.
}

const DOCUMENT_STATUS_MESSAGES: Record<string, MessageDescriptor> = {
  ACTIVE: POLICY_FILTER_MESSAGES.dsActive,
  PENDING: POLICY_FILTER_MESSAGES.dsPending,
  PROCESSING: POLICY_FILTER_MESSAGES.dsProcessing,
  DECLINED: POLICY_FILTER_MESSAGES.dsDeclined,
  COMPLETED: POLICY_FILTER_MESSAGES.dsCompleted,
}

const PAYMENT_STATUS_MESSAGES: Record<string, MessageDescriptor> = {
  PAID: POLICY_FILTER_MESSAGES.psPaid,
  FAILED: POLICY_FILTER_MESSAGES.psFailed,
  PENDING: POLICY_FILTER_MESSAGES.psPaymentPending,
  AUTO_PAY: POLICY_FILTER_MESSAGES.psAutoPay,
  NOT_APPLICABLE: POLICY_FILTER_MESSAGES.psNotApplicable,
  PAGA_POR_SU_CUENTA: POLICY_FILTER_MESSAGES.psPagaPorSuCuenta,
}

type Option = {value: string; label: string}

const translate = (
  intl: IntlShape,
  options: Option[],
  messages: Record<string, MessageDescriptor>
): Option[] =>
  options.map((o) => ({
    value: o.value,
    label: messages[o.value] ? intl.formatMessage(messages[o.value]) : o.label,
  }))

/**
 * Catálogos compartidos con las etiquetas traducidas. Se calculan en render y
 * **no** mutan los consts de `helpers/catalogs.ts` (guía §17.10/§17.17); los
 * valores en inglés siguen viajando a la API.
 */
export const translatedCatalogs = (intl: IntlShape) => ({
  immigration: translate(intl, IMMIGRATION_STATUS_CHOICES, CATALOG_MESSAGES),
  gender: translate(intl, GENDER_OPTIONS, CATALOG_MESSAGES),
  metal: translate(intl, METAL_LEVELS, CATALOG_MESSAGES),
  document: translate(intl, DOCUMENT_STATUS_OPTIONS, DOCUMENT_STATUS_MESSAGES),
  payment: translate(intl, PAYMENT_STATUS_OPTIONS, PAYMENT_STATUS_MESSAGES),
  carrierStatus: CARRIER_STATUS_MESSAGES.map((o) => ({
    value: o.value,
    label: intl.formatMessage(o.message),
  })),
  commissionStatus: COMMISSION_STATUS_MESSAGES.map((o) => ({
    value: o.value,
    label: intl.formatMessage(o.message),
  })),
})

/** Nombre completo de un contacto cuando el backend no manda `fullName`. */
export const fullName = (first?: string | null, last?: string | null) =>
  `${first ?? ''} ${last ?? ''}`.trim()

/** Sufijo de desborde de los chips: `Aetna, Cigna +3`. */
export const overflowSuffix = (rest: number) => ` +${rest}`
/** Rango de fechas de un chip: `2026-01-01 → 2026-03-31`. */
export const dateRange = (from?: string, to?: string) => [from, to].filter(Boolean).join(' → ')
